/**
 * V2Step — wrapper shell for every onboarding step.
 * Renders: progress rail, back button, step label, title, subtitle, content.
 */
import * as React from 'react';
import { V2C, fontDisplay, fontBody } from './tokens';
import { ArrowLeftIcon } from './primitives';

interface V2StepProps {
  stepIdx: number;       // 0-based index of current step
  totalSteps: number;    // total number of steps (8)
  title: string;
  subtitle?: string;
  onBack?: () => void;
  savedAt?: string | null;
  saveStatus?: 'idle' | 'saving' | 'saved' | 'error';
  children: React.ReactNode;
  /** Forward-direction animation (default true) */
  animDir?: 'forward' | 'back';
}

export default function V2Step({
  stepIdx, totalSteps, title, subtitle, onBack, savedAt, saveStatus, children, animDir = 'forward',
}: V2StepProps) {
  const animName = animDir === 'back' ? 'v2slideInLeft' : 'v2slideInRight';

  // stepIdx < 0 signals the Account step — hide progress rail and step label.
  const showProgress = stepIdx >= 0;

  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column',
        width: '100%', maxWidth: '100%', minWidth: 0,
        minHeight: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        fontFamily: fontBody,
        animation: `${animName} 0.28s cubic-bezier(0.16,1,0.3,1)`,
      }}
    >
      {/* Progress rail — hidden on Account step */}
      {showProgress && <V2ProgressRail current={stepIdx} total={totalSteps} />}

      {/* Top bar: back + step label + save status */}
      <div style={{
        display: 'flex', alignItems: 'center', padding: '12px 20px 0',
        gap: 12,
      }}>
        {onBack ? (
          <button onClick={onBack} aria-label="Go back" style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: '4px', borderRadius: 8, display: 'flex', alignItems: 'center',
            flexShrink: 0,
          }}>
            <ArrowLeftIcon size={16} color={V2C.mute} />
          </button>
        ) : <div style={{ width: 24 }} />}

        <div style={{ flex: 1, minWidth: 0 }}>
          {showProgress && (
            <span style={{ fontSize: 11, fontWeight: 700, color: V2C.mute, letterSpacing: 0.4 }}>
              Step {stepIdx + 1} of {totalSteps}
            </span>
          )}
        </div>

        {saveStatus && saveStatus !== 'idle' && (
          <V2SaveIndicator status={saveStatus} savedAt={savedAt} />
        )}
      </div>

      {/* Title + subtitle */}
      <div style={{ padding: '16px 20px 20px' }}>
        <h1 style={{
          fontFamily: fontDisplay, fontSize: 26, fontWeight: 500,
          color: V2C.ink, margin: 0, lineHeight: 1.1, letterSpacing: -0.5,
        }}>
          {title}
        </h1>
        {subtitle && (
          <p style={{
            fontFamily: fontBody, fontSize: 14, color: V2C.mute,
            margin: '8px 0 0', lineHeight: 1.5,
          }}>
            {subtitle}
          </p>
        )}
      </div>

      {/* Step content */}
      <div style={{
        flex: 1,
        width: '100%', maxWidth: '100%', minWidth: 0,
        padding: '0 20px 100px',
        overflowY: 'auto', overflowX: 'hidden',
        boxSizing: 'border-box',
      }}>
        {children}
      </div>
    </div>
  );
}

// ── Progress rail ─────────────────────────────────────────────────────────────
function V2ProgressRail({ current, total }: { current: number; total: number }) {
  return (
    <div style={{ display: 'flex', gap: 3, padding: '0 20px', paddingTop: 12 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          flex: 1, height: 3, borderRadius: 99,
          background: i <= current ? V2C.brand : V2C.line,
          transition: 'background 0.3s ease',
        }} />
      ))}
    </div>
  );
}

// ── Save indicator ────────────────────────────────────────────────────────────
function V2SaveIndicator({ status, savedAt }: { status: string; savedAt?: string | null }) {
  // eslint-disable-next-line react-hooks/purity
  const nowRef = React.useRef(Date.now());

  function friendlyAgo(iso: string) {
    const ms = nowRef.current - new Date(iso).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  let label = '';
  let color = V2C.mute;
  if (status === 'saving') { label = 'Saving…'; color = V2C.mute; }
  else if (status === 'error') { label = 'Save failed'; color = V2C.err; }
  else if (status === 'saved') { label = savedAt ? `Saved · ${friendlyAgo(savedAt)}` : 'Saved'; }

  if (!label) return null;
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, color,
      padding: '4px 8px', borderRadius: 99,
      background: 'rgba(255,255,255,0.55)',
    }}>
      {label}
    </span>
  );
}
